import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/api";
import { useRegister } from "@/lib/queries";
import { Spinner } from "@/components/ui";
import { AuthShell, ErrorBox, Field } from "./LoginPage";

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useRegister();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await register.mutateAsync({
        name,
        email,
        password,
        // Blank is allowed — the API falls back to "<name>'s workspace".
        workspaceName: workspaceName.trim() || undefined,
        // Browser timezone beats asking; the user can change it in settings.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  return (
    <AuthShell title={t("auth.registerTitle")} subtitle={t("auth.registerSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("auth.name")} value={name} onChange={setName} autoComplete="name" required />
        <Field
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <Field
          label={t("auth.password")}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={8}
          required
        />
        <Field
          label={t("auth.workspaceName")}
          value={workspaceName}
          onChange={setWorkspaceName}
          hint={t("auth.workspaceHint")}
        />
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        <button type="submit" disabled={register.isPending} className="btn-primary w-full">
          {register.isPending ? <Spinner /> : null}
          {register.isPending ? t("auth.creating") : t("auth.signUp")}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        {t("auth.haveAccount")}{" "}
        <Link to="/login" className="font-medium text-primary-700 hover:underline">
          {t("auth.signIn")}
        </Link>
      </p>
    </AuthShell>
  );
}
